import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { FaHistory, FaInbox, FaPen, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { userAppData } from '../MainApp.js';
import { getAvailableForms } from './formsUserApi';
import { getFormIcon } from './formIcons';
import Header from '../common/header';
import './forms.css';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };

/** Displays the forms the authenticated user has permission to complete. */
export default function FormsHome() {
    const navigate = useNavigate();
    const formsData = userAppData.formsData || {};
    const authorised = Number(formsData.perms ?? 0) === 1;
    const canCreateForms = formsData.canCreateForms === true || Number(formsData.canCreateForms ?? 0) === 1;
    const adminFormIds = new Set(Array.isArray(formsData.formsAdmin) ? formsData.formsAdmin.map(String) : []);
    const activeStepCount = new Set(Array.isArray(formsData.activeSteps) ? formsData.activeSteps.map(String).filter(Boolean) : []).size;
    const [loading, setLoading] = useState(true);
    const [forms, setForms] = useState([]);
    const [searchText, setSearchText] = useState('');

    /** Filters by a case-insensitive substring anywhere in the form name. */
    const visibleForms = useMemo(() => {
        const searchValue = searchText.trim().toLocaleLowerCase();
        if (!searchValue) return forms;
        return forms.filter((form) => String(form.name || '').toLocaleLowerCase().includes(searchValue));
    }, [forms, searchText]);

    useEffect(() => {
        if (!authorised) return;
        async function loadForms() {
            setLoading(true);
            try {
                const availableForms = await getAvailableForms();
                setForms([...availableForms].sort((first, second) => String(first.name || '').localeCompare(String(second.name || ''), undefined, { sensitivity: 'base' })));
            } catch (error) {
                console.error('Failed to load available forms', error);
                toast.error('❌ Failed to load your forms. Please try again.', TOAST_CONFIG);
            } finally { setLoading(false); }
        }
        loadForms();
    }, [authorised]);

    if (!authorised) return <Navigate to="/app" replace />;

    return <div className="main_div"><div className="internal_main_div forms-page-shell">
        <Header title="Forms" subtitle="Select a form to complete." rightContent={<div className="forms-home-header-actions">
            <button type="button" className="module-page-header__button forms-home-my-responses" onClick={() => navigate('/app/forms/responses/mine')}><FaHistory /> My responses</button>
            {canCreateForms && <button type="button" className="module-page-header__button" onClick={() => navigate('/app/forms/manage?create=1')}><FaPlus /> Create form</button>}
        </div>} />
        {activeStepCount > 0 && <button type="button" className="forms-active-steps-summary" onClick={() => navigate('/app/forms/steps')} aria-label={`${activeStepCount} form stages awaiting completion`}><FaInbox aria-hidden="true" /><div><strong>{activeStepCount}</strong><span>{activeStepCount === 1 ? 'form stage is' : 'form stages are'} waiting for you to complete</span></div></button>}
        {loading && <section className="forms-page-card"><p>Loading forms...</p></section>}
        {!loading && forms.length === 0 && <section className="forms-page-card"><p>No forms are currently available to you.</p></section>}
        {!loading && forms.length > 0 && <label className="forms-home-search"><span className="forms-visually-hidden">Search forms</span><input type="search" value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="Search forms" autoComplete="off" /></label>}
        {!loading && forms.length > 0 && visibleForms.length === 0 && <section className="forms-page-card forms-home-search-empty"><p>No forms match your search.</p></section>}
        {!loading && visibleForms.length > 0 && <div className="forms-user-grid">{visibleForms.map((form) => { const FormIcon = getFormIcon(form.icon); const canEdit = adminFormIds.has(String(form.formId)); const liveResponses = Math.max(0, Number(form.liveResponses) || 0); const actionedResponses = Math.max(0, Number(form.actionedResponses) || 0); const unactionedResponses = Math.max(0, liveResponses - actionedResponses); return <article className="forms-user-card" key={form.formId}>
            {canEdit && <button type="button" className="forms-user-card-edit" onClick={() => navigate(`/app/forms/manage?edit=${encodeURIComponent(form.formId)}`)} aria-label={`Edit ${form.name || 'form'}`} title="Edit form"><FaPen /></button>}
            <button type="button" className="forms-user-card-open" onClick={() => navigate(`/app/forms/${form.formId}`)} aria-label={`Open ${form.name || 'form'}`}>
                <div className="forms-user-card-icon"><FormIcon aria-hidden="true" /></div><h2>{form.name || 'Unnamed form'}</h2>
                <p>{form.description || 'No description has been provided.'}</p>
            </button>
            {canEdit && <div className="forms-user-card-stats" aria-label={`${liveResponses} live responses, ${unactionedResponses} unactioned`}>
                <span><strong>{liveResponses}</strong> Total responses</span>
                <span><strong>{unactionedResponses}</strong> Unactioned</span>
            </div>}
            {canEdit && <button type="button" className="forms-user-card-responses" onClick={() => navigate(`/app/forms/${encodeURIComponent(form.formId)}/responses`)}><FaInbox /> View responses</button>}
            {!canEdit && <button type="button" className="forms-user-card-responses" onClick={() => navigate(`/app/forms/responses/mine?formId=${encodeURIComponent(form.formId)}`)}><FaHistory /> My responses</button>}
        </article>; })}</div>}
    </div></div>;
}
